/**
 * =============================================================================
 * IDS CRM Web - Hierarchy family tree (Gareth 2026-08-21)
 * =============================================================================
 *
 * Who is involved with any robot: Distributor → Depot → Customer → Site →
 * Asset as one family tree, filtered by where it was entered. The focus node
 * is highlighted; every node's name opens its record, and the ⌖ control
 * re-focuses the tree at that node — so the same page walks up and down the
 * chain. Direct customers hang under the implicit Peacock Technology →
 * Bellshill spine (no records exist for either, by design).
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './Hierarchy.css';

const TYPE_META = {
  distributor: { badge: 'Distributor', className: 'distributor' },
  virtual_distributor: { badge: 'Distributor', className: 'distributor virtual' },
  depot: { badge: 'Depot', className: 'depot' },
  virtual_depot: { badge: 'Depot', className: 'depot virtual' },
  customer: { badge: 'Customer', className: 'customer' },
  site: { badge: 'Site', className: 'site' },
  asset: { badge: 'Asset', className: 'asset' },
  bucket: { badge: '—', className: 'bucket' }
};

/** Types the ⌖ re-focus control makes sense for (they are real entry points). */
const REFOCUSABLE = {
  distributor: 'distributor',
  depot: 'depot',
  customer: 'customer',
  site: 'site',
  asset: 'asset'
};

function TreeNode({ node, isFocusPath, focusRef }) {
  const meta = TYPE_META[node.type] || TYPE_META.bucket;
  const refocusType = REFOCUSABLE[node.type];

  return (
    <li className={`tree-node ${meta.className}`}>
      <div className={`tree-row${node.focus ? ' is-focus' : ''}`} ref={node.focus ? focusRef : undefined}>
        <span className={`tree-badge ${meta.className.split(' ')[0]}`}>{meta.badge}</span>
        <span className="tree-label">
          {node.link ? <Link to={node.link}>{node.label}</Link> : node.label}
        </span>
        {node.sublabel && <span className="tree-sublabel">{node.sublabel}</span>}
        {refocusType && !node.focus && !String(node.id).startsWith('ptl') && (
          <Link
            to={`/hierarchy/${refocusType}/${node.id}`}
            className="tree-refocus"
            title={`Re-focus the tree on ${node.label}`}
          >
            ⌖
          </Link>
        )}
        {node.focus && <span className="tree-focus-chip">You are here</span>}
      </div>
      {node.children?.length > 0 && (
        <ul className="tree-children">
          {node.children.map(child => (
            <TreeNode key={`${child.type}-${child.id}`} node={child} focusRef={focusRef} />
          ))}
        </ul>
      )}
    </li>
  );
}

function Hierarchy() {
  const { type, id } = useParams();
  const { authFetch } = useAuth();
  const [tree, setTree] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const focusRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`/api/v1/hierarchy/${type}/${id}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.message || 'Failed to build the hierarchy');
      setTree(body.root);
    } catch (err) {
      setError(err.message);
      setTree(null);
    } finally {
      setLoading(false);
    }
  }, [authFetch, type, id]);

  useEffect(() => { load(); }, [load]);

  // Deep trees: bring the entry point into view rather than making the user
  // hunt for the highlight.
  useEffect(() => {
    if (!loading && focusRef.current && typeof focusRef.current.scrollIntoView === 'function') {
      focusRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loading, tree]);

  return (
    <div className="hierarchy-page">
      <div className="page-header">
        <div>
          <h1>Hierarchy</h1>
          <p className="hierarchy-hint">
            Distributor → Depot → Customer → Site → Asset. Names open the record;
            ⌖ re-focuses the tree at that level.
          </p>
        </div>
      </div>

      {loading && <div className="loading-page">Building the tree...</div>}
      {error && <div className="form-error">{error}</div>}
      {!loading && !error && tree && (
        <div className="hierarchy-tree-card">
          <ul className="tree-root">
            <TreeNode node={tree} focusRef={focusRef} />
          </ul>
        </div>
      )}
    </div>
  );
}

export default Hierarchy;
